/**
 * toolNameVocabulary — the one shared vocabulary for `technical_trace.tool`, the field that names
 * the tool a trace row was produced by.
 *
 * Two surfaces print this field to the same technician: `ConversationPanel`'s collapsed trace box
 * ("כלי", mounted from the conversation page, the ticket's `ai-chat` tab and the employee workspace)
 * and `HandoffCenterPage`'s evidence drawer ("כלי", fed through `handoffCenterModel.evidenceFrom`).
 * Both rendered the bare value, so a snake_case English identifier reached the operator inside a
 * Hebrew row — the same failure `statusVocabulary`, `evidenceSourceVocabulary` and
 * `actionTypeVocabulary` exist to prevent, one field over. On the pilot's default reply path this is
 * every reply: `conversation_service` writes `tool=CONVERSATION_TOOL_NAME` on the reply trace, so
 * the row directly above `מקור מידע` read `כלי: agent_conversation_reply`.
 *
 * NOT `ACTION_TYPE_LABEL`, and not a widening of it. That map is keyed on an *action* token — what an
 * employee did or proposes to do — and shares no key with this field at all. The near miss is the
 * ESET read: the run timeline records the action `eset_device_security_lookup` for a call to the
 * *tool* `eset_get_device_security_status`, so the two fields name one underlying read with two
 * different strings. A map keyed on tool names therefore cannot be the action map, which is the
 * pairing `actionTypeVocabulary.test.js` already pins from the other side.
 *
 * The key set is the trace builder's closed set, and it is *not* syntactically closed at the call
 * site: four of the nine `tool=` arguments in `conversation_service.py` hand over the bare local
 * `tool` rather than a literal, and that local is `intent.tool or "analyze_ticket"`, fed by
 * `conversation_intents.OPERATION_TOOLS` and `detect_intent`. `toolNameVocabulary.test.jsx` resolves
 * that chain instead of skipping it, and fails on any *unrecognised* variable argument — the hole
 * §3.69 named for the action map, closed here for this field.
 */
export const TRACE_TOOL_LABEL = {
  // The reply path. The registry's own description is "answer a technician's chat message
  // conversationally — no tool call, no state change", and the human response it accompanies is the
  // employee talking, so the label names the turn rather than claiming a capability was exercised.
  agent_conversation_reply: "מענה בשיחה",
  // The registry tool that reads a ticket and produces a structured diagnosis. Distinct from
  // `conversation_intents`' intent labels ("טיפול בקריאה" / "משימת אבחון") on purpose: those name
  // what the technician asked for, this names the tool that ran.
  analyze_ticket: "ניתוח קריאה",
  // The same words `EsetSpecialistPage.ESET_READ_LABELS` and `ACTION_TYPE_LABEL` use for this read,
  // pinned equal by the test — one ESET read must not read two ways depending on the field it
  // arrived in.
  eset_get_device_security_status: "מצב אבטחה של מכשיר",
  // Reachable on the pilot: `detect_intent` maps a backup check to this tool, the registry does not
  // carry it, and the resulting row is a decline telling the technician the capability is absent —
  // so this token is exactly the one that must not arrive as an English identifier.
  acronis_get_backup_status: "מצב גיבוי",
  // The manager's routing decision. Spelled with its object because `ConversationPanel` prints it in
  // the same box as the `handoff` row labelled "ניתוב", which names the two ends of the handoff;
  // this names the tool that chose them.
  agent_manager_route: "ניתוב השיחה לעובד",
};

/**
 * The Hebrew name of one tool token, or the token itself when this build has no name for it.
 *
 * Returning the token is deliberate, and is the same choice `actionTypeLabel` makes: an unmapped
 * token is a token no producer sweep has reached yet, and showing the operator the producer's own
 * identifier is the honest failure. Inventing a Hebrew word for a tool the vocabulary does not
 * describe would be a guess presented as a label, and would hide the drift the parity test exists to
 * catch.
 */
export function traceToolLabel(token) {
  if (typeof token !== "string" || !token) return null;
  return TRACE_TOOL_LABEL[token] ?? token;
}
